/**
 * DeclarationCard — Individual declaration history row.
 * Extracted from Payroll.jsx for maintainability.
 */
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Briefcase, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeclarationCard = ({ decl, getStatusBadge, onRetrySuccess }) => {
  const [retrying, setRetrying] = useState(false);
  const canRetry = decl.status === 'local_only' || decl.status === 'error';

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/payroll/pajemploi/retry/${decl.id}`);
      if (res.data.success) {
        toast.success(`Déclaration envoyée à Pajemploi — Réf: ${res.data.reference_number}`);
        if (onRetrySuccess) onRetrySuccess();
      } else {
        // Show translated error
        const errorDetails = res.data.error_details?.errors;
        if (errorDetails?.length > 0) {
          toast.error(errorDetails[0].message);
        } else {
          toast.error(res.data.message || 'Échec du renvoi');
        }
        if (onRetrySuccess) onRetrySuccess(); // Refresh to show updated status
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du renvoi à Pajemploi');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`declaration-card-${decl.id}`}>
      <CardContent className="pt-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-100)' }}>
              <Briefcase className="h-5 w-5 text-[var(--primary-600)]" />
            </div>
            <div>
              <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{decl.child_name}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {new Date(decl.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
                {decl.reference_number && ` — Réf: ${decl.reference_number}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(decl.status)}
            <span className="text-lg font-bold" style={{ color: 'var(--success)' }}>{decl.net_salary?.toFixed(2)}€</span>
          </div>
        </div>

        {decl.error_message && decl.status !== 'submitted' && decl.status !== 'confirmed' && (
          <Alert className="mt-3 border-[var(--secondary-200)] bg-[var(--secondary-50)]">
            <AlertTriangle className="h-4 w-4 text-[var(--secondary-500)]" />
            <AlertDescription className="text-xs" style={{ color: 'var(--secondary-600)' }}>{decl.error_message}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
          <div><span style={{ color: 'var(--text-muted)' }}>Brut</span><p className="font-medium">{decl.gross_salary?.toFixed(2)}€</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Heures</span><p className="font-medium">{decl.hours_worked}h</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Cotis. salariales</span><p className="font-medium">{decl.employee_contributions?.toFixed(2)}€</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Créé le</span><p className="font-medium">{new Date(decl.created_at).toLocaleDateString('fr-FR')}</p></div>
        </div>

        {/* Retry button for local_only or error declarations */}
        {canRetry && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--neutral-200)' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={retrying}
              className="gap-2 w-full sm:w-auto"
              style={{ borderColor: 'var(--primary-300)', color: 'var(--primary-500)' }}
              data-testid={`retry-declaration-${decl.id}`}
            >
              {retrying ? (
                <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Renvoi en cours…</>
              ) : (
                <><RefreshCw className="h-3.5 w-3.5" /> Renvoyer à Pajemploi</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
